import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PersonService } from '../../services/personService';
import type { PersonSearchResult } from '../../types';

/**
 * GlobalSearchBar Component
 *
 * Header search with autocomplete for finding people
 *
 * Features:
 * - Debounced search (300ms)
 * - Autocomplete dropdown with top 5 results
 * - Click result → navigate to person profile
 * - Enter key → navigate to full search results page
 * - Recent searches (stored in localStorage)
 * - Loading and empty states
 * - Clear button
 * - Responsive (compact on mobile)
 *
 * @component
 * @returns {JSX.Element} Global search bar
 */
export const GlobalSearchBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ===== State =====

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PersonSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showRecentSearches, setShowRecentSearches] = useState(false);

  // ===== Load Recent Searches =====

  useEffect(() => {
    try {
      const saved = localStorage.getItem('recentSearches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Failed to load recent searches:', error);
    }
  }, []);

  // ===== Debounced Search =====

  useEffect(() => {
    // Don't search if term is too short
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      setOpen(false);
      setShowRecentSearches(searchTerm.trim().length === 0 && recentSearches.length > 0);
      return;
    }

    // Hide recent searches when typing
    setShowRecentSearches(false);

    // Debounce search
    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        const results = await PersonService.searchPersons(searchTerm, {});

        // Limit to top 5 results for autocomplete
        setSearchResults(results.slice(0, 5));
        setOpen(true);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, recentSearches.length]);

  // ===== Event Handlers =====

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectPerson = (person: PersonSearchResult | null) => {
    if (person) {
      navigate(`/people/${person.id}`);
      setSearchTerm('');
      setOpen(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && searchTerm.trim()) {
      event.preventDefault();
      event.stopPropagation();

      // Save to recent searches
      saveRecentSearch(searchTerm.trim());

      // Navigate to full search results page
      const query = searchTerm.trim();
      setSearchTerm('');
      setOpen(false);
      setShowRecentSearches(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleRecentSearchClick = (term: string) => {
    // Navigate to search results
    setSearchTerm('');
    setOpen(false);
    setShowRecentSearches(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const handleClearRecentSearches = (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      localStorage.removeItem('recentSearches');
      setRecentSearches([]);
      setShowRecentSearches(false);
    } catch (error) {
      console.error('Failed to clear recent searches:', error);
    }
  };

  const handleFocus = () => {
    if (searchTerm.trim().length === 0 && recentSearches.length > 0) {
      setShowRecentSearches(true);
      setOpen(true);
    }
  };

  const saveRecentSearch = (term: string) => {
    try {
      const updated = [
        term,
        ...recentSearches.filter(s => s !== term),
      ].slice(0, 10);

      localStorage.setItem('recentSearches', JSON.stringify(updated));
      setRecentSearches(updated);
    } catch (error) {
      console.error('Failed to save recent search:', error);
    }
  };

  // ===== Helper Functions =====

  /**
   * Format person's life years (birth-death)
   */
  const getLifeYears = useCallback((person: PersonSearchResult): string => {
    const birthYear = person.birthDate?.substring(0, 4);
    const deathYear = person.deathDate?.substring(0, 4);

    if (birthYear && deathYear) {
      return `${birthYear}-${deathYear}`;
    } else if (birthYear) {
      return `b. ${birthYear}`;
    } else if (deathYear) {
      return `d. ${deathYear}`;
    }
    return '';
  }, []);

  /**
   * Get person's initials for avatar
   */
  const getInitials = useCallback((person: PersonSearchResult): string => {
    const first = person.firstName?.[0] || '';
    const last = person.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase();
  }, []);

  // ===== Render =====

  return (
    <Autocomplete
      freeSolo
      open={open && (searchResults.length > 0 || showRecentSearches || (searchTerm.trim().length >= 2 && searchResults.length === 0 && !loading))}
      onClose={() => {
        setOpen(false);
        setShowRecentSearches(false);
      }}
      options={searchResults}
      loading={loading}
      value={null}
      inputValue={searchTerm}
      onChange={(_, value) => handleSelectPerson(value as PersonSearchResult | null)}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return `${option.firstName} ${option.lastName}`;
      }}
      filterOptions={(x) => x} // Disable built-in filtering (we handle it)
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={isMobile ? 'Search...' : 'Search people...'}
          size="small"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading && (
                  <InputAdornment position="end">
                    <CircularProgress color="inherit" size={18} />
                  </InputAdornment>
                )}
                {searchTerm && !loading && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      edge="end"
                      sx={{ mr: -0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            ),
          }}
          sx={{
            minWidth: isMobile ? 40 : 200,
            maxWidth: isMobile ? 200 : 400,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
              },
            },
          }}
        />
      )}
      renderOption={(props, person) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              fontSize: '0.875rem',
            }}
          >
            {getInitials(person)}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {person.firstName} {person.lastName}
            </Typography>
            {getLifeYears(person) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getLifeYears(person)}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      PaperComponent={({ children }) => (
        <Paper
          elevation={8}
          sx={{
            mt: 0.5,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Recent Searches */}
          {showRecentSearches && recentSearches.length > 0 && (
            <>
              <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                  RECENT SEARCHES
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleClearRecentSearches}
                  title="Clear recent searches"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {recentSearches.map((term, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleRecentSearchClick(term)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto' }}>
                    <HistoryIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={term}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                </ListItemButton>
              ))}
              {!loading && searchTerm.trim().length < 2 && <Divider />}
            </>
          )}

          {/* Search Results */}
          {!showRecentSearches && children}

          {/* View All Results Button */}
          {searchTerm.trim().length >= 2 && searchResults.length > 0 && !loading && !showRecentSearches && (
            <>
              <Divider />
              <ListItemButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const query = searchTerm.trim();
                  saveRecentSearch(query);
                  setSearchTerm('');
                  setOpen(false);
                  setShowRecentSearches(false);
                  navigate(`/search?q=${encodeURIComponent(query)}`);
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  backgroundColor: 'action.hover',
                }}
              >
                <SearchIcon fontSize="small" color="primary" />
                <Typography variant="body2" color="primary" fontWeight="medium">
                  View all results for "{searchTerm}"
                </Typography>
              </ListItemButton>
            </>
          )}

          {/* No Results Message */}
          {searchTerm.trim().length >= 2 && searchResults.length === 0 && !loading && !showRecentSearches && (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No people found. Press Enter to see all results.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      noOptionsText="No people found"
      loadingText="Searching..."
    />
  );
};
